// Client-side session for the email/password login (and access-code logins,
// which behave the same but may hide sign-out and may resume onto the same
// bound browser). The token (a non-expiring JWT) lives in localStorage and is
// sent as `Authorization: Bearer <token>` on dashboard API calls (see
// dashboard-api.ts). Clearing browser data or logging out drops it — the user
// then logs in again.
//
// An access-code session that cannot self-serve sign-out instead moves its
// token into a "resume" slot on logout, so the SAME browser can silently
// re-bind on the next login without spending another seat on the code.

const TOKEN_KEY = "ow-auth-token";
const EMAIL_KEY = "ow-auth-email";
const CAN_LOGOUT_KEY = "ow-auth-can-logout";
const KIND_KEY = "ow-auth-kind";
const RESUME_KEY = "ow-auth-resume";

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function getEmail(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(EMAIL_KEY);
}

export function isAuthenticated(): boolean {
  return getToken() !== null;
}

/** Whether the current session should show a sign-out control. Defaults to
 * true when absent so pre-existing sessions (stored before this key existed)
 * still show sign-out. */
export function canLogout(): boolean {
  if (typeof window === "undefined") return true;
  const stored = window.localStorage.getItem(CAN_LOGOUT_KEY);
  return stored === null ? true : stored === "true";
}

/** Resume token saved by a code session's logout, if any. */
export function getResumeToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(RESUME_KEY);
}

/** Clears the saved resume token — called once it has been consumed (or
 * superseded) by a successful login, so a stale token can't be replayed. */
export function clearResumeToken(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(RESUME_KEY);
}

export function setSession(token: string, email: string, opts?: { canLogout?: boolean; kind?: string }): void {
  window.localStorage.setItem(TOKEN_KEY, token);
  window.localStorage.setItem(EMAIL_KEY, email);
  window.localStorage.setItem(CAN_LOGOUT_KEY, String(opts?.canLogout ?? true));
  if (opts?.kind) window.localStorage.setItem(KIND_KEY, opts.kind);
  else window.localStorage.removeItem(KIND_KEY);
}

/** Clears the active session only. Never touches the resume token — a
 * logged-out code session's resume slot must survive this. */
export function clearSession(): void {
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(EMAIL_KEY);
  window.localStorage.removeItem(CAN_LOGOUT_KEY);
  window.localStorage.removeItem(KIND_KEY);
}

/** Authorization header for authenticated fetches, or empty when logged out. */
export function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** The admin screen for access codes (S94). Exact match, so /admin/access-x
 *  and /admin/accessfoo stay with the data admin panel. */
export function isAccessAdminPath(path: string): boolean {
  return path === "/admin/access" || path.startsWith("/admin/access/");
}

/** Called on a 401 from a gated route — drop the session and send to /login.
 *  On the access admin screen, reload in place instead: the page then renders
 *  the login form and returns to the same screen after sign-in. */
export function handleUnauthorized(): void {
  clearSession();
  if (typeof window === "undefined" || window.location.pathname.startsWith("/login")) return;
  if (isAccessAdminPath(window.location.pathname)) window.location.reload();
  else window.location.href = "/login";
}

/** Explicit logout — clears the session and returns to the login screen. A
 * code session (kind "code", or one stored with canLogout=false) hands its
 * token off to the resume slot first, so the same browser can silently
 * re-bind on next login instead of spending another seat on the code. */
export function logout(): void {
  if (typeof window !== "undefined") {
    const kind = window.localStorage.getItem(KIND_KEY);
    const token = getToken();
    if (token && (kind === "code" || !canLogout())) {
      window.localStorage.setItem(RESUME_KEY, token);
    }
  }
  clearSession();
  if (typeof window !== "undefined") window.location.href = "/login";
}
